import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';

// Curated list of icons appropriate for cleaning services, features, values, etc.
const USABLE_ICONS = [
  // Cleaning & Home
  'SprayCan', 'Droplets', 'Wind', 'Home', 'Building2', 'Warehouse', 'DoorOpen',
  // Services
  'Wrench', 'Settings', 'Zap', 'Shield', 'Sparkles', 'Star', 'Award', 'BadgeCheck',
  'ThumbsUp', 'Heart', 'Smile', 'Users', 'UserCheck', 'Handshake',
  // Technology & Innovation
  'Cpu', 'Zap', 'Wifi', 'Globe', 'Monitor', 'Smartphone', 'Camera',
  // Nature & Environment
  'Leaf', 'Flower2', 'TreePine', 'Sun', 'Cloud', 'Recycle',
  // Vehicles & Transport
  'Car', 'Truck', 'Bus',
  // Business
  'Briefcase', 'ChartBar', 'TrendingUp', 'Target', 'Clock', 'Calendar', 'Mail',
  'Phone', 'MapPin', 'CreditCard', 'ShoppingCart', 'Package',
  // Actions
  'Play', 'Pause', 'ChevronRight', 'ArrowRight', 'Check', 'X',
  // Misc
  'Lightbulb', 'Eye', 'BookOpen', 'MessageCircle', 'Share2',
];

// Simple category grouping for the picker
const ICON_CATEGORIES = [
  {
    name: '🏠 Kebersihan & Rumah',
    icons: ['SprayCan', 'Droplets', 'Wind', 'Home', 'Building2', 'Warehouse', 'DoorOpen'],
  },
  {
    name: '🛠️ Layanan',
    icons: ['Wrench', 'Settings', 'Zap', 'Shield', 'Sparkles', 'Star', 'Award', 'BadgeCheck', 'ThumbsUp', 'Heart', 'Smile', 'Users', 'UserCheck', 'Handshake'],
  },
  {
    name: '💻 Teknologi',
    icons: ['Cpu', 'Wifi', 'Globe', 'Monitor', 'Smartphone', 'Camera'],
  },
  {
    name: '🌿 Alam',
    icons: ['Leaf', 'Flower2', 'TreePine', 'Sun', 'Cloud', 'Recycle'],
  },
  {
    name: '🚗 Kendaraan',
    icons: ['Car', 'Truck', 'Bus'],
  },
  {
    name: '💼 Bisnis',
    icons: ['Briefcase', 'ChartBar', 'TrendingUp', 'Target', 'Clock', 'Calendar', 'Mail', 'Phone', 'MapPin', 'CreditCard', 'ShoppingCart', 'Package'],
  },
  {
    name: '🎯 Aksi',
    icons: ['Play', 'Pause', 'ChevronRight', 'ArrowRight', 'Check', 'X', 'Lightbulb', 'Eye', 'BookOpen', 'MessageCircle', 'Share2'],
  },
];

export default function LucideIconPicker({ value, onChange, label }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  // Filter icons by search term
  const filtered = search.trim()
    ? USABLE_ICONS.filter(name => name.toLowerCase().includes(search.toLowerCase()))
    : null;

  // Get current icon component if value is set
  const CurrentIcon = value ? LucideIcons[value] : null;

  const handleSelect = (iconName) => {
    onChange(iconName);
    setOpen(false);
    setSearch('');
  };

  return (
    <div>
      {label && <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>}
      <div className="relative">
        {/* Trigger button */}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white hover:border-primary/50 transition-colors flex items-center gap-2 text-left"
        >
          {CurrentIcon ? (
            <CurrentIcon size={18} className="text-primary shrink-0" />
          ) : (
            <span className="text-gray-400 text-lg leading-none shrink-0">□</span>
          )}
          <span className="flex-1 text-gray-700 truncate">
            {value ? value.replace(/([A-Z])/g, ' $1').trim() : 'Pilih icon...'}
          </span>
          <span className="text-gray-400 text-xs shrink-0">{open ? '▲' : '▼'}</span>
        </button>

        {/* Dropdown */}
        {open && (
          <div className="absolute z-50 top-full left-0 mt-1 w-full min-w-[320px] bg-white border border-gray-200 rounded-xl shadow-xl p-3 max-h-80 overflow-y-auto">
            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cari icon..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary outline-none mb-3"
              autoFocus
            />

            {filtered ? (
              // Search results
              <div className="flex flex-wrap gap-2">
                {filtered.map(name => {
                  const Icon = LucideIcons[name];
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleSelect(name)}
                      className={`p-2 rounded-lg hover:bg-primary/10 transition-colors flex flex-col items-center gap-1 w-16 ${
                        value === name ? 'bg-primary/20 ring-2 ring-primary' : ''
                      }`}
                      title={name}
                    >
                      {Icon ? <Icon size={22} className="text-gray-700" /> : <span className="text-xs">?</span>}
                      <span className="text-[9px] text-gray-500 leading-tight text-center truncate w-full">
                        {name.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <p className="text-xs text-gray-400 py-4 text-center w-full">Tidak ada icon dengan nama "{search}"</p>
                )}
              </div>
            ) : (
              // Categorized view
              <div className="space-y-4">
                {ICON_CATEGORIES.map(cat => (
                  <div key={cat.name}>
                    <h5 className="text-xs font-semibold text-gray-500 mb-2">{cat.name}</h5>
                    <div className="flex flex-wrap gap-2">
                      {cat.icons.map(name => {
                        const Icon = LucideIcons[name];
                        if (!Icon) return null;
                        return (
                          <button
                            key={name}
                            type="button"
                            onClick={() => handleSelect(name)}
                            className={`p-2 rounded-lg hover:bg-primary/10 transition-colors flex flex-col items-center gap-1 w-16 ${
                              value === name ? 'bg-primary/20 ring-2 ring-primary' : ''
                            }`}
                            title={name}
                          >
                            <Icon size={22} className="text-gray-700" />
                            <span className="text-[9px] text-gray-500 leading-tight text-center truncate w-full">
                              {name.replace(/([A-Z])/g, ' $1').trim()}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click-outside handler */}
      {open && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setOpen(false); setSearch(''); }}
        />
      )}
    </div>
  );
}